import React, { PureComponent } from 'react';
import Transition from 'react-transition-group/Transition';
import { TweenMax, Circ } from 'gsap';
import CharacterMenuGroup from './CharacterMenuGroup';
import CategoryMenuGroup from './CategoryMenuGroup';
import GoToPage from './GoToPage';
import MenuGroupTabs from './MenuGroupTabs';
import BasicButton from '../@common/Buttons/BasicButton';
import DrawnCircleXIcon from 'assets/icons/DrawnCircleXIcon';
import { remCalc } from '../../util/style-util';
import { WorkbookToCGrouping } from 'types';
import withStyles from 'components/@layout/withStyles';
import { FONT_GSS_PRINT_WIDE } from 'themes/default';

type Props = {
  show: boolean;
  content: Array<WorkbookToCGrouping>;
  primaryBookColor: string;
  totalPages: number;
  currentPageNumber: number;
  workbookCode: string;
  toggleMenu: () => void;
  classes: any;
};

type State = {
  activeGroup: string;
  duration: number;
  workbookCode: string;
};

const enterDuration = 1000;
const exitDuration = 500;

class PageSelectorMenu extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      activeGroup: 'search',
      duration: enterDuration,
      workbookCode: ''
    };
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    state.duration = props.show ? enterDuration : exitDuration;
    if (state.workbookCode !== props.workbookCode) {
      return { activeGroup: 'search', workbookCode: props.workbookCode };
    }
    return null;
  }

  buildMenuGroups() {
    const { content, currentPageNumber, totalPages } = this.props;
    const sections: any = {};

    content &&
      content.forEach(section => {
        sections[section.grouping] = this.createGroup(section);
      });

    sections.search = (
      <GoToPage
        currentPage={currentPageNumber}
        totalPages={totalPages}
        closeMenu={this.closeMenu}
      />
    );

    return sections;
  }

  createGroup = (section: WorkbookToCGrouping) => {
    return section.grouping !== '123' ? (
      <CategoryMenuGroup
        categories={section.categories}
        color={this.props.primaryBookColor}
        closeMenu={this.closeMenu}
        name={section.grouping}
        categoryType={section.type}
        hasIcon={section.hasIcon}
      />
    ) : (
      <CharacterMenuGroup
        section={section}
        characters={section.categories[0].characters}
        color={this.props.primaryBookColor}
        closeMenu={this.closeMenu}
        name={section.grouping}
        //was type={section.type}, but that was undefined. Grouping contains the data needed by CharMenuGroup for the right instructions
        type={section.grouping}
      />
    );
  };

  changeTOCGroup = (groupName: string) => {
    return () => this.setState({ activeGroup: groupName });
  };

  closeMenu = () => this.props.toggleMenu();

  animate = (menu: HTMLElement) => {
    TweenMax.fromTo(
      menu,
      this.state.duration / 1000,
      {
        y: this.props.show ? 0 : -10,
        opacity: this.props.show ? 0 : 1
      },
      {
        y: this.props.show ? -10 : 0,
        opacity: this.props.show ? 1 : 0,
        ease: Circ.easeOut
      }
    );
  };

  render() {
    /* eslint-disable react/jsx-handler-names */
    const { activeGroup: active } = this.state;
    const { pageMenu, closeButton } = this.props.classes;
    const menuGroups = this.buildMenuGroups();

    return (
      <Transition
        in={this.props.show}
        timeout={this.state.duration}
        onEnter={this.animate}
        onExit={this.animate}
        mountOnEnter={true}
        unmountOnExit={true}
      >
        <nav className={pageMenu}>
          <BasicButton
            icon={DrawnCircleXIcon}
            onClick={this.closeMenu}
            buttonStyles={[closeButton]}
            aria-label="Close menu"
            tabIndex="0"
          />
          <MenuGroupTabs
            activeGroup={active}
            content={this.props.content}
            handleTOCGroupChange={this.changeTOCGroup}
          />
          {menuGroups[active]}
        </nav>
      </Transition>
    );
  }
}

const styles = {
  pageMenu: {
    display: 'grid',
    gridTemplateColumns: remCalc(100, 'auto', 100),
    gridTemplateRows: remCalc(100, 'max-content', 25),
    gridTemplateAreas: "'header header header' '. main .'",
    backgroundColor: 'white',
    border: '3px solid black',
    borderRadius: '15px',
    position: 'absolute',
    bottom: '9vh',
    fontFamily: FONT_GSS_PRINT_WIDE,
    minHeight: remCalc(536),
    maxWidth: '98%',
    '&:after': {
      content: "''",
      position: 'absolute',
      borderLeft: '20px solid transparent',
      borderRight: '20px solid transparent',
      borderTop: '20px solid white',
      width: 0,
      bottom: -15,
      right: '50%',
      transform: 'translateX(20px)'
    }
  },
  closeButton: {
    width: remCalc(40),
    height: remCalc(40),
    padding: '0',
    position: 'absolute',
    top: 10,
    right: 10,
    '&:active': {
      transform: 'translateY(3px)'
    }
  }
};
export default withStyles(styles)(PageSelectorMenu) as any;
